import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Eye } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import { isAuthenticated } from '../lib/authUtils';


const ProductCard = React.memo(({ product }) => {
  const { addToCart, addToWishlist, isInWishlist, addToCompare } = useCart();
  const navigate = useNavigate();

  const handleAddToCart = (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast({
        title: 'Login Required',
        description: 'Please login to add items to your cart.',
        variant: 'destructive',
      });
      navigate('/customer-login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }

    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`
    });
  };

  const handleAddToWishlist = (e) => {
    e.preventDefault();
    addToWishlist(product);
    toast({
      title: 'Added to wishlist',
      description: `${product.name} has been added to your wishlist.`
    });
  };

  const handleAddToCompare = (e) => {
    e.preventDefault();
    addToCompare(product);
    toast({
      title: 'Added to compare',
      description: `${product.name} has been added to compare list.`
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group relative">
      {/* Badges */}
      <div className="absolute top-2 left-2 z-10 flex flex-col space-y-1">
        {product.badges && product.badges.includes('sale') && (
          <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">SALE</span>
        )}
        {product.badges && product.badges.includes('hot') && (
          <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded font-semibold">HOT</span>
        )}
        {product.badges && product.badges.includes('new') && (
          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded font-semibold">NEW</span>
        )}
      </div>

      {/* Quick Actions */}
      <div className="absolute top-2 right-2 z-10 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleAddToWishlist}
          className="bg-white p-2 rounded-full shadow hover:bg-red-600 hover:text-white transition"
          title="Add to Wishlist"
        >
          <Heart size={16} fill={isInWishlist(product.id) ? 'currentColor' : 'none'} />
        </button>
        <button
          onClick={handleAddToCompare}
          className="bg-white p-2 rounded-full shadow hover:bg-red-600 hover:text-white transition"
          title="Add to Compare"
        >
          <Eye size={16} />
        </button>
      </div>

      {/* Image */}
      <Link to={`/product/${product.slug}`} className="block">
        <div className="aspect-square overflow-hidden bg-gray-50">
          <img
            src={product.image || '/placeholder.jpg'}
            alt={product.name}
            loading="lazy"
            onError={(e) => {
              e.target.src = '/placeholder.jpg';
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        <div className="text-xs text-gray-500 mb-1">SKU: {product.sku}</div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-semibold text-gray-900 mb-2 hover:text-red-600 transition line-clamp-2 h-12">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        <div className="flex items-center mb-2">
          <div className="flex text-sm">{renderStars(Math.round(product.rating))}</div>
          <span className="text-xs text-gray-500 ml-2">({product.reviews})</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-lg font-bold text-gray-900">${product.price.toFixed(2)}</span>
            {product.originalPrice && (
              <span className="text-sm text-gray-400 line-through ml-2">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        {/* Stock Status */}
        <div className="mb-3">
          {product.stock > 0 ? (
            <span className="text-xs text-green-600 font-medium">In Stock ({product.stock})</span>
          ) : (
            <span className="text-xs text-red-600 font-medium">Out of Stock</span>
          )}
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={product.stock !== undefined && product.stock !== null && product.stock <= 0}
          className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          <ShoppingCart size={18} />
          <span>Add to Cart</span>
        </button>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
